import { FC, useMemo } from 'react';
import type { IconType } from 'react-icons';
import {
    TbActivity,
    TbBrowser,
    TbCalendar,
    TbCube,
    TbDevices,
    TbId,
    TbPyramid,
    TbUser,
    TbWorldWww,
} from 'react-icons/tb';

import type {
    WorkspaceResourceAdvancedFilterSetter,
    WorkspaceResourceFilterDefinitionSetter,
    WorkspaceResourceFilterSetter,
} from '@/hooks/useWorkspaceResourceFilters';
import {
    createFilterableColumn,
    FilterableColumn,
    ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import WorkspaceResourceFiltersRow from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/WorkspaceResourceFiltersRow';
import { SavedFilterResourceType, type AuditLogQueryFacets } from '@/services';
import type { AuditLog } from '@/types';
import type { AdvancedFilterDefinition } from '@/shared';
import { ListViewColumnDataType } from '@/types';

import {
    getAuditActorName,
    getAuditPermission,
    normalizeAuditAction,
    normalizeAuditResource,
} from '../auditLogFilters';

interface AuditLogFiltersRowProps {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    auditLogs: AuditLog[];
    canManageSharedFilters: boolean;
    facets?: AuditLogQueryFacets;
    filters: ListFilterRule[];
    onChange: WorkspaceResourceFilterSetter;
    onAdvancedChange: WorkspaceResourceAdvancedFilterSetter;
    resourceId?: string;
    onDefinitionChange: WorkspaceResourceFilterDefinitionSetter;
}

const icon = (Icon: IconType) => <Icon size='0.875rem' />;

const uniqueOptions = (values: Array<string | undefined>) =>
    Array.from(new Set(values.filter((value): value is string => Boolean(value))))
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({ label: value, value }));

const AuditLogFiltersRow: FC<AuditLogFiltersRowProps> = ({
    advancedFilter,
    auditLogs,
    canManageSharedFilters,
    facets,
    filters,
    onChange,
    onAdvancedChange,
    onDefinitionChange,
    resourceId,
}): JSX.Element => {
    const columns = useMemo<FilterableColumn[]>(
        () => [
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOptions: uniqueOptions(
                    facets?.actions.map(normalizeAuditAction) ??
                        auditLogs.map((log) => normalizeAuditAction(log.action)),
                ),
                icon: icon(TbActivity),
                key: 'action',
                label: 'Action',
                order: 0,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOptions: uniqueOptions(
                    facets?.resources.map(normalizeAuditResource) ??
                        auditLogs.map((log) => normalizeAuditResource(log.entityType)),
                ),
                icon: icon(TbCube),
                key: 'resource',
                label: 'Resource',
                order: 1,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOptions: uniqueOptions(facets?.actors ?? auditLogs.map(getAuditActorName)),
                icon: icon(TbUser),
                key: 'actor',
                label: 'Actor',
                order: 2,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOptions: uniqueOptions(facets?.permissions ?? auditLogs.map(getAuditPermission)),
                icon: icon(TbPyramid),
                key: 'permission',
                label: 'Permission',
                order: 3,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOptions: uniqueOptions(facets?.deviceTypes ?? auditLogs.map((log) => log.deviceType)),
                icon: icon(TbDevices),
                key: 'deviceType',
                label: 'Device Type',
                order: 4,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SELECT,
                filterOptions: uniqueOptions(facets?.browsers ?? auditLogs.map((log) => log.browser)),
                icon: icon(TbBrowser),
                key: 'browser',
                label: 'Browser',
                order: 5,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SHORT_TEXT,
                icon: icon(TbWorldWww),
                key: 'ipAddress',
                label: 'IP Address',
                order: 6,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SHORT_TEXT,
                icon: icon(TbId),
                key: 'entityId',
                label: 'Resource ID',
                order: 7,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.DATE,
                icon: icon(TbCalendar),
                key: 'createdAt',
                label: 'Occurred Date',
                order: 8,
            }),
        ],
        [auditLogs, facets],
    );

    return (
        <WorkspaceResourceFiltersRow
            advancedFilter={advancedFilter}
            canManageSharedFilters={canManageSharedFilters}
            columns={columns}
            compatibleResourceLabel='Audit Logs'
            docsLabel='saved Audit Log filters'
            emptyColumnsDescription='Audit filters are available after the workspace records an event.'
            emptyHint='Filter, save, or reuse saved filters to investigate workspace events.'
            filterableResourceLabel='Audit Logs'
            filters={filters}
            horizontalPadding={0}
            onChange={onChange}
            onAdvancedChange={onAdvancedChange}
            onDefinitionChange={onDefinitionChange}
            resourceId={resourceId}
            resourceLabel='Audit Logs'
            resourceScopedLabel='These Audit Logs'
            resourceType={SavedFilterResourceType.AUDIT_LOG}
            showEmptyHint={false}
            workspaceScopeDescription='Workspace filters are available to workspace owners and administrators with access to Audit Logs.'
            workspaceScopeTitle='Available to workspace administrators'
        />
    );
};

export default AuditLogFiltersRow;
